// frontend/src/ExplorerLogModal.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from 'react-modal';
import Graph from 'react-graph-vis'; // Assuming react-graph-vis is installed
import './ExplorerLogModal.css'; // Create this CSS file

// --- Reusable Collapsible List (copied from AppExplorer for self-containment) ---
function CollapsibleList({ title, items, limit = 20, initiallyExpanded = false }) {
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
    if (!items || items.length === 0) { return (<div className="collapsible-list empty"><h5>{title} (0)</h5></div>); }
    const toggleExpand = () => setIsExpanded(!isExpanded);
    const displayedItems = isExpanded ? items : items.slice(0, limit);
    return (
        <div className="collapsible-list">
            <h5 onClick={toggleExpand} style={{ cursor: 'pointer' }}>
                {title} ({items.length}) {items.length > limit && (isExpanded ? ' [-]' : ' [+]')}
            </h5>
            {isExpanded && (<><ul>{displayedItems.map((item, index) => (<li key={index}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>))}</ul>{items.length > limit && (<button onClick={toggleExpand} className="toggle-expand">Show Less</button>)}</>)}
            {!isExpanded && items.length > limit && (<button onClick={toggleExpand} className="toggle-expand">Show More ({items.length - limit} more)</button>)}
            {!isExpanded && items.length <= limit && displayedItems.length > 0 && (<ul>{displayedItems.map((item, index) => (<li key={index}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>))}</ul>)}
        </div>
    );
}
// --- End Collapsible List ---

// --- Modal Style (copied from AppExplorer for consistency) ---
const customModalStyles = {
    content: {
        top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)',
        maxWidth: '90%', width: '1000px', // Adjust width as needed
        maxHeight: '90vh', overflow: 'hidden', // Let internal divs scroll
        padding: '0', // Remove padding from main modal container
        border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
        backgroundColor: '#fff', zIndex: 1001,
        display: 'flex', flexDirection: 'column' // Use flex column for layout
    },
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1000 },
};
// --- End Modal Style ---

const ExplorerLogModal = ({
    isOpen,
    onClose,
    logData, // Expects the full object returned by /api/load_log/
    isLoading,
    error
}) => {
    const [activeStepIndex, setActiveStepIndex] = useState(-1);
    const [isAutoPlayingGraph, setIsAutoPlayingGraph] = useState(false);
    const autoPlayIntervalRef = useRef(null);

    // Set initial active step when data loads
    useEffect(() => {
        if (logData?.displayDataHistory?.length > 0) {
            setActiveStepIndex(logData.displayDataHistory.length - 1); // Start at the last step
        } else {
            setActiveStepIndex(-1);
        }
        // Stop autoplay if modal re-opens or data changes
        setIsAutoPlayingGraph(false);
        if (autoPlayIntervalRef.current) {
             clearInterval(autoPlayIntervalRef.current);
             autoPlayIntervalRef.current = null;
        }
    }, [logData]); // Dependency on logData

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (autoPlayIntervalRef.current) {
                clearInterval(autoPlayIntervalRef.current);
            }
        };
    }, []);


    // --- Graph Visualization Helpers (adapted from AppExplorer) ---
     const graphOptions = { /* ... options remain the same ... */
        layout: { hierarchical: false },
        physics: { solver: 'forceAtlas2Based', forceAtlas2Based: { gravitationalConstant: -55, centralGravity: 0.015, springLength: 110, springConstant: 0.08, damping: 0.5, avoidOverlap: 0.6 }, stabilization: { enabled: true, iterations: 150, updateInterval: 25 } },
        edges: { color: { color: "#848484", highlight: '#ff0000', hover: '#d3d3d3' }, arrows: { to: { enabled: true, scaleFactor: 0.6 } }, font: { size: 9, color: '#a0a0a0', strokeWidth: 0 }, smooth: { enabled: true, type: "continuous", roundness: 0.5 }, width: 1, hoverWidth: 1.5 },
        nodes: { shape: 'dot', size: 12, borderWidth: 1.5, font: { size: 11, color: '#333' }, color: { border: '#666', background: '#D2E5FF', highlight: { border: '#2B7CE9', background: '#87CEFA' }, hover: { border: '#2B7CE9', background: '#ADD8E6' } } },
        interaction: { tooltipDelay: 150, hover: true, navigationButtons: false, /* Disable built-in nav */ keyboard: true, zoomView: true, dragView: true },
        height: '100%', width: '100%'
    };

     const getGraphDataForStep = useCallback((stepIndex) => {
        // ... (logic copied directly from AppExplorer's getGraphDataForStep) ...
         if (!logData || !logData.displayDataHistory || stepIndex < 0 || stepIndex >= logData.displayDataHistory.length) {
             return { nodes: [], edges: [], nodeLabelMap: {} };
         }
         const stepData = logData.displayDataHistory[stepIndex];
         if (!stepData || !stepData.graphStructure) {
            console.warn(`Modal: No valid graph data found for step index ${stepIndex}`);
            return { nodes: [], edges: [], nodeLabelMap: {} };
         }
         const structure = stepData.graphStructure || { nodes: [], edges: [] };
         const highlights = stepData.graphHighlights || { currentNodes: [], connectedNodes: [] };

         const nodes = (structure.nodes || []).map(node => {
              if (!node || typeof node.id === 'undefined') return null;
              let extraStyle = {};
              if ((highlights.currentNodes || []).includes(node.id)) extraStyle = { color: { background: '#FFBDBD', border: '#FF0000' }, size: 20, shape: 'star', font: { color: '#A52A2A', size: 13, weight: 'bold' }, borderWidth: 2 };
              else if ((highlights.connectedNodes || []).includes(node.id)) extraStyle = { color: { background: '#FFEBCD', border: '#FFA500' }, size: 16, shape: 'diamond', borderWidth: 1.5, font: { size: 12 } };
              return { ...node, label: String(node.label ?? ''), ...extraStyle };
          }).filter(Boolean);

         const edges = (structure.edges || []).map(edge => {
              if (!edge || typeof edge.from === 'undefined' || typeof edge.to === 'undefined') return null;
              const isFromCurrent = (highlights.currentNodes || []).includes(edge.from);
              const isToCurrent = (highlights.currentNodes || []).includes(edge.to);
              let edgeStyle = {};
              if (isFromCurrent || isToCurrent) edgeStyle = { color: { color: '#FF6347', highlight: '#FF0000' }, width: 2.5 };
              // Keep articleId and title if they exist in the data
              return { ...edge, articleId: edge.articleId, title: edge.title, ...edgeStyle };
          }).filter(Boolean);

          const nodeLabelMap = nodes.reduce((acc, node) => { if (node) acc[node.id] = node.label; return acc; }, {});
         return { nodes, edges, nodeLabelMap };
    }, [logData]); // Depends only on logData

    const activeGraphData = getGraphDataForStep(activeStepIndex);

    // --- Step Navigation Handlers (adapted from AppExplorer) ---
    const handlePreviousStep = () => { if (isAutoPlayingGraph) return; setActiveStepIndex(prev => Math.max(0, prev - 1)); };
    const handleNextStep = () => { if (isAutoPlayingGraph) return; setActiveStepIndex(prev => Math.min((logData?.displayDataHistory?.length || 1) - 1, prev + 1)); };
    const handleToggleAutoPlayGraph = () => {
         if (isAutoPlayingGraph) {
            setIsAutoPlayingGraph(false); clearInterval(autoPlayIntervalRef.current); autoPlayIntervalRef.current = null;
        } else {
             const historyLength = logData?.displayDataHistory?.length || 0;
             if (historyLength <= 1) return;
             setIsAutoPlayingGraph(true);
             let startIndex = activeStepIndex >= 0 && activeStepIndex < historyLength - 1 ? activeStepIndex : 0;
             setActiveStepIndex(startIndex); // Ensure starting from a valid index or 0
             autoPlayIntervalRef.current = setInterval(() => {
                setActiveStepIndex(prev => {
                    const nextIndex = prev + 1;
                     if (nextIndex >= historyLength) {
                        setIsAutoPlayingGraph(false); clearInterval(autoPlayIntervalRef.current); autoPlayIntervalRef.current = null;
                        return prev; // Stay at the last step
                     }
                    return nextIndex;
                });
            }, 3000);
        }
    };

    const canGoPrevious = activeStepIndex > 0;
    const canGoNext = activeStepIndex < (logData?.displayDataHistory?.length || 1) - 1;
    const activeStepData = (logData?.displayDataHistory && activeStepIndex >= 0 && activeStepIndex < logData.displayDataHistory.length)
        ? logData.displayDataHistory[activeStepIndex]
        : null;


    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={customModalStyles}
            contentLabel="Knowledge Exploration Details"
            ariaHideApp={false}
        >
            {/* Modal Header */}
            <div className="explorer-modal-header">
                <h2>Exploration Details: {logData?.conversationName || logData?.uniqueId?.substring(0, 8) || 'N/A'}</h2>
                <button onClick={onClose} className="modal-close-button top-right">×</button>
            </div>

            {/* Loading/Error State */}
            {isLoading && <div className="loading-fullpage">Loading Log Data...</div>}
            {!isLoading && error && <div className="error" style={{ margin: '20px' }}>Error loading log: {error}</div>}
            {!isLoading && !error && !logData && <div className="loading-fullpage">No log data available.</div>}

            {/* Main Content Area */}
            {!isLoading && !error && logData && (
                <div className="explorer-modal-content">
                    {/* Left Column: Step Details */}
                    <div className="explorer-modal-left">
                        {logData.initialQuery && <p className="initial-query"><strong>Initial Query:</strong> {logData.initialQuery}</p>}
                        <h3 className="step-details-title">Step Details ({activeStepIndex + 1} / {logData.displayDataHistory?.length || 0})</h3>

                        {activeStepData ? (
                            <div className="active-chat-step-details">
                                {/* Use CollapsibleList for cleaner display */}
                                {activeStepData.stepCurrentNodes?.length >= 0 && <CollapsibleList title="Nodes Explored" items={activeStepData.stepCurrentNodes || []} limit={10} initiallyExpanded={false} />}
                                {activeStepData.stepNewlySelectedStatements?.length >= 0 && <CollapsibleList title="Statements Added" items={activeStepData.stepNewlySelectedStatements || []} limit={10} initiallyExpanded={false} />}
                                {activeStepData.stepConnectedStatements?.length >= 0 && <CollapsibleList title="Connected Statements Found" items={activeStepData.stepConnectedStatements || []} limit={15} initiallyExpanded={false} />}
                                {Array.isArray(activeStepData.stepRagContext) && activeStepData.stepRagContext.length > 0 && <CollapsibleList title="RAG Context (This Step)" items={activeStepData.stepRagContext} limit={5} initiallyExpanded={false} />}

                                {/* LLM Response & Reasoning in Details */}
                                {activeStepData.stepLlmResponse && (
                                    <details className="step-details-toggle">
                                        <summary>LLM Analysis (JSON)</summary>
                                        <pre>{JSON.stringify(activeStepData.stepLlmResponse, null, 2)}</pre>
                                    </details>
                                )}
                                {activeStepData.stepReasoning && (
                                    <details className="step-details-toggle">
                                        <summary>LLM Reasoning</summary>
                                        <pre style={{ whiteSpace: 'pre-wrap' }}>{activeStepData.stepReasoning}</pre>
                                    </details>
                                )}
                                 {activeStepData.stepFinalResponse && activeStepIndex === logData.displayDataHistory.length - 1 && (
                                    <details className="step-details-toggle" open>
                                        <summary>Final Synthesized Response</summary>
                                        <pre className="final-response">{activeStepData.stepFinalResponse}</pre>
                                    </details>
                                )}
                            </div>
                        ) : (
                            <p>Select a step using the graph controls.</p>
                        )}
                    </div>

                    {/* Right Column: Graph */}
                    <div className="explorer-modal-right">
                        <div className="graph-container-modal">
                             <div className="graph-header-modal">
                                  <span>Graph Visualization (Step {activeStepIndex + 1})</span>
                                  <div className="step-nav-buttons-modal">
                                     {(logData?.displayDataHistory?.length || 0) > 1 && (
                                         <button onClick={handleToggleAutoPlayGraph} disabled={isLoading} className={`autoplay-button ${isAutoPlayingGraph ? 'active' : ''}`}> {isAutoPlayingGraph ? 'Pause' : 'Play'} </button>
                                     )}
                                    <button onClick={handlePreviousStep} disabled={!canGoPrevious || isAutoPlayingGraph}>Prev</button>
                                    <button onClick={handleNextStep} disabled={!canGoNext || isAutoPlayingGraph}>Next</button>
                                  </div>
                             </div>
                             <div className="graph-vis-area">
                                {activeGraphData && activeGraphData.nodes && activeGraphData.nodes.length > 0 ? (
                                    <Graph
                                        key={`modal-graph-step-${activeStepIndex}-${logData.uniqueId}`} // More specific key
                                        graph={activeGraphData}
                                        options={graphOptions}
                                        // events={{ click: handleGraphClick }} // Add click handler if needed later
                                    />
                                ) : ( <div className="graph-placeholder"> {activeStepIndex < 0 ? "Select a step." : "No graph data for this step."} </div> )}
                             </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Footer */}
            <div className="explorer-modal-footer">
                <button onClick={onClose} className="modal-button secondary">Close</button>
            </div>
        </Modal>
    );
};

export default ExplorerLogModal;